import clsx from '../utils/clsx';
import styles from '../styles/Battlefield.module.css';
import MapIcon from './MapIcon';
import { BUILD_TOOLS } from '../hooks/useBattlefieldBuild';

/**
 * The gestures, beside the tools that use them.
 *
 * A gesture is not a tool - Fill, Erase, Zone and Dig all take any of
 * them - but it is the same kind of choice, made at the same moment, so
 * it belongs on the same rail rather than in the bar underneath.
 */
const GESTURES = [
  { key: 'rect', label: 'Shape', icon: 'shape', hint: 'Drag out a shape' },
  { key: 'line', label: 'Line', icon: 'line', hint: 'Drag a wall at any angle' },
  { key: 'brush', label: 'Brush', icon: 'brush', hint: 'Drag freehand' },
];

/** One radiogroup of icon buttons: the tools, or the gestures. */
function RailGroup({ label, items, value, testPrefix, onPick }) {
  return (
    <div className={styles.railGroup} role="radiogroup" aria-label={label}>
      {items.map((t) => (
        <button
          key={t.key}
          type="button"
          role="radio"
          aria-checked={value === t.key}
          aria-label={t.label}
          data-testid={`${testPrefix}${t.key}`}
          className={clsx(styles.railButton, value === t.key && styles.railButtonActive)}
          onClick={() => onPick(t.key)}
          title={`${t.label} - ${t.hint}`}
        >
          <MapIcon name={t.icon} />
        </button>
      ))}
    </div>
  );
}

/**
 * BattlefieldToolRail - the tools, down the side of the map.
 *
 * Every control the build bar had was on screen at once, which made a
 * wall of buttons and ate the map's height. The rail holds only the
 * CHOICES - which tool, which gesture - and the bar beneath the map holds
 * the options for whichever one is selected. Icons with tooltips, because
 * a rail of words is a menu.
 *
 * `BUILD_TOOLS` has carried `icon`, `label` and `hint` since the tools
 * were written, for exactly this.
 */
export default function BattlefieldToolRail({ build }) {
  return (
    <div className={styles.toolRail} data-testid="battlefield-tool-rail">
      <RailGroup label="Build tool" items={BUILD_TOOLS} value={build.tool} testPrefix="tool-" onPick={build.setTool} />
      <div className={styles.railDivider} />
      <RailGroup
        label="Gesture"
        items={GESTURES}
        value={build.gesture}
        testPrefix="gesture-"
        onPick={build.setGesture}
      />
    </div>
  );
}
